import React, { type ReactNode, useCallback, useState, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ArrowLeft from '@atlaskit/icon/glyph/arrow-left';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';

import {
	MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID,
	MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID,
	MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID,
} from '../../common/constants';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import { ReviewTilesType } from '../../common/constants/steps';
import type { ThirdPartyEntity } from '../../common/types';
import { PageDescription, PageHeading } from '../../common/ui';
import { CloseAndReturnButton } from '../../common/ui/close-and-return-button';
import { StepFooter, StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard, StepProgressBar } from '../../common/ui/step-progress-bar';
import { useContentForThirdParty, useUrlForThirdParty } from '../../common/utils';
import { useX2CFlag } from '../../common/utils/use-flag';
import { useProjectData, useThirdPartyState } from '../../controllers/import-state';
import { useStartImportController } from '../../controllers/start-import';
import { useAccessTokenModalState, useUIState } from '../../controllers/ui-state';
import { useProductSteps } from '../../controllers/utils';
import { useThirdPartyFieldConfig } from '../../services/third-party-field-config';
import { useThirdPartyMultiEntityStatuses } from '../../services/third-party-multi-entity-statuses';
import { FieldsMapping } from './fields-mapping';
import messages from './messages';
import { ProjectHierarchy } from './project-hierarchy';
import { ReviewTiles } from './review-tiles';
import { StatusMapping } from './status-mapping';

type Props = {
	thirdParty: SupportedThirdParty;
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
	tiles: ReviewTilesType[];
	isMultipleEntitiesSupported?: boolean;
};

export const ThirdPartyReviewScreen = ({
	thirdParty,
	onNext,
	onReturn,
	onCloseAndReturnToUploadStep,
	tiles,
	isMultipleEntitiesSupported = false,
}: Props) => {
	const { productSteps, currentStepIndex } = useProductSteps();
	const { formatMessage } = useIntl();

	const { startImport, isImportLoading, isStartImportDisabled, hasImportErrorOccurred } =
		useStartImportController({ thirdParty, isMultipleEntitiesSupported });
	const [projectData] = useProjectData();
	const [selectedTile, setSelectedTile] = useState<ReviewTilesType | undefined>(undefined);
	const [
		{
			selectedEntityId,
			selectedEntityName,
			selectedEntities,
			thirdPartyImportSettings,
			thirdPartyFieldConfigState,
			thirdPartyStatusMapping,
		},
		{ setThirdPartyState, setThirdPartyImportSettings },
	] = useThirdPartyState();

	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();
	const { fetchThirdPartyFieldConfig, isLoading, hasErrorOccurred } = useThirdPartyFieldConfig({
		thirdParty,
		requestAuthenticationToken: setRequestAuthenticationToken,
	});
	const {
		fetchThirdPartyMultiEntityStatuses,
		isLoading: isStatusMappingLoading,
		hasErrorOccurred: statusMappingError,
	} = useThirdPartyMultiEntityStatuses({
		thirdParty,
		requestAuthenticationToken: setRequestAuthenticationToken,
	});
	const { onShowFlag, onDismissFlag } = useX2CFlag();
	const [
		{
			hasUnsavedFieldMappingChanges,
			hasUnsavedHierarchyMappingChanges,
			hasUnsavedStatusMappingChanges,
		},
		{ setUIState },
	] = useUIState();
	const { reviewScreenDescription } = useContentForThirdParty(thirdParty);
	const { reviewScreenDescriptionURL } = useUrlForThirdParty(thirdParty, projectData.type);

	const onFetchThirdPartyStatusMapping = useCallback(() => {
		selectedEntities &&
			selectedEntities.length > 0 &&
			tiles.includes(ReviewTilesType.STATUS) &&
			fetchThirdPartyMultiEntityStatuses(
				selectedEntities.map((entity) => entity.id),
				projectData,
			).then((data) => {
				setThirdPartyState({ thirdPartyStatusMapping: data });
			});
	}, [
		fetchThirdPartyMultiEntityStatuses,
		projectData,
		selectedEntities,
		setThirdPartyState,
		tiles,
	]);

	const onFetchThirdPartyFieldConfig = useCallback(() => {
		if (!ff('x2c-clickup-importer-multi-list')) {
			selectedEntityId &&
				fetchThirdPartyFieldConfig(selectedEntityId, projectData).then((data) => {
					setThirdPartyState({ thirdPartyFieldConfigState: data });
				});
		} else {
			const entityId = selectedEntityId || (selectedEntities && selectedEntities[0]?.id);
			const entities: ThirdPartyEntity[] =
				selectedEntities || (entityId && [{ id: entityId, name: selectedEntityName ?? '' }]) || [];
			entityId &&
				entities.length > 0 &&
				fetchThirdPartyFieldConfig(entityId, projectData, entities).then((data) => {
					setThirdPartyState({ thirdPartyFieldConfigState: data });
				});
		}
	}, [
		fetchThirdPartyFieldConfig,
		projectData,
		selectedEntities,
		selectedEntityId,
		selectedEntityName,
		setThirdPartyState,
	]);

	useEffect(() => {
		!thirdPartyFieldConfigState && onFetchThirdPartyFieldConfig();
	}, [onFetchThirdPartyFieldConfig, thirdPartyFieldConfigState]);

	useEffect(() => {
		!thirdPartyStatusMapping && onFetchThirdPartyStatusMapping();
	}, [onFetchThirdPartyStatusMapping, thirdPartyStatusMapping]);

	const onStartImport = useCallback(() => {
		startImport({ projectData, onNext });
	}, [startImport, projectData, onNext]);

	const onSelectedTile = useCallback(
		(tile: ReviewTilesType) => {
			setSelectedTile(tile);
			onDismissFlag(MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID);
			onDismissFlag(MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID);
			onDismissFlag(MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID);
		},
		[onDismissFlag],
	);

	useEffect(
		() => () => {
			onDismissFlag(MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID);
			onDismissFlag(MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID);
			onDismissFlag(MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID);
			setUIState({
				hasUnsavedFieldMappingChanges: false,
				hasUnsavedHierarchyMappingChanges: false,
				hasUnsavedStatusMappingChanges: false,
			});
		},
		[onDismissFlag, setUIState],
	);

	const onResetSelectedTile = useCallback(() => {
		setSelectedTile(undefined);
		if (hasUnsavedFieldMappingChanges) {
			onShowFlag(MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID);
			setUIState({ hasUnsavedFieldMappingChanges: false });
		}
		if (hasUnsavedHierarchyMappingChanges) {
			onShowFlag(MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID);
			setUIState({ hasUnsavedHierarchyMappingChanges: false });
		}
		if (hasUnsavedStatusMappingChanges) {
			onShowFlag(MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID);
			setUIState({ hasUnsavedStatusMappingChanges: false });
		}
	}, [
		hasUnsavedFieldMappingChanges,
		hasUnsavedHierarchyMappingChanges,
		hasUnsavedStatusMappingChanges,
		onShowFlag,
		setUIState,
	]);

	const renderStepFooterComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<StepFooterStandard
					alignRight
					onReturn={onReturn}
					shouldStartImportOnNext
					onClickNext={onStartImport}
					isLoading={isImportLoading}
					shouldDisableReturn={isImportLoading}
					shouldDisableNext={
						isStartImportDisabled ||
						isLoading ||
						hasErrorOccurred ||
						(tiles.includes(ReviewTilesType.STATUS) && isStatusMappingLoading) ||
						statusMappingError
					}
					hasImportErrorOccurred={hasImportErrorOccurred}
				/>
			);
		}
		return (
			<FooterWrapper>
				<StepFooter
					onReturn={onReturn}
					shouldStartImportOnNext
					onClickNext={onStartImport}
					isLoading={isImportLoading}
					shouldDisableReturn={isImportLoading}
					shouldDisableNext={
						isStartImportDisabled ||
						isLoading ||
						hasErrorOccurred ||
						(tiles.includes(ReviewTilesType.STATUS) && isStatusMappingLoading) ||
						statusMappingError
					}
					hasImportErrorOccurred={hasImportErrorOccurred}
				/>
			</FooterWrapper>
		);
	}, [
		hasErrorOccurred,
		hasImportErrorOccurred,
		isImportLoading,
		isLoading,
		isStartImportDisabled,
		isStatusMappingLoading,
		onReturn,
		onStartImport,
		statusMappingError,
		tiles,
	]);

	const renderContent = () => {
		const returnButton = (
			<Button
				onClick={onResetSelectedTile}
				appearance="subtle"
				testId="project-create-importer-upload-flow.ui.third-party-review-screen.back-button"
				iconBefore={<ArrowLeft label="" size="small" />}
			>
				{formatMessage(messages.backToReview)}
			</Button>
		);

		if (selectedTile === ReviewTilesType.HIERARCHY) {
			return (
				<>
					{returnButton}
					<ProjectHierarchy thirdParty={thirdParty} />
				</>
			);
		}

		if (selectedTile === ReviewTilesType.FIELDS && thirdPartyFieldConfigState) {
			return (
				<>
					{returnButton}
					<FieldsMapping thirdParty={thirdParty} />
				</>
			);
		}

		if (selectedTile === ReviewTilesType.STATUS && thirdPartyStatusMapping) {
			return (
				<>
					{returnButton}
					<StatusMapping thirdParty={thirdParty} />
				</>
			);
		}

		return (
			<>
				<PageHeading>{formatMessage(messages.heading)}</PageHeading>
				<PageDescription>
					{reviewScreenDescription
						? formatMessage(reviewScreenDescription, {
								link: (chunks: ReactNode[]) => (
									<Button
										href={reviewScreenDescriptionURL}
										target="_blank"
										appearance="link"
										spacing="none"
									>
										{chunks}
									</Button>
								),
							})
						: ''}
				</PageDescription>
				<TileWrapper data-testid="project-create-importer-upload-flow.ui.third-party-review-screen.tile-wrapper">
					<ReviewTiles
						onSetSelectedTile={onSelectedTile}
						onRefetchFieldMapping={onFetchThirdPartyFieldConfig}
						thirdPartyImportSettings={thirdPartyImportSettings}
						onSetThirdPartyImportSettings={setThirdPartyImportSettings}
						thirdParty={thirdParty}
						isLoading={isLoading}
						isStatusMappingLoading={isStatusMappingLoading}
						tiles={tiles}
						hasErrorOccurred={hasErrorOccurred || !thirdPartyFieldConfigState}
						hasStatusMappingErrorOccurred={statusMappingError || !thirdPartyStatusMapping}
					/>
					{renderStepFooterComponent}
				</TileWrapper>
			</>
		);
	};

	const progressBarComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<>
					<ProgressBarStandard
						productSteps={productSteps}
						currentStepIndex={currentStepIndex}
						onClose={onCloseAndReturnToUploadStep}
					/>
					<Box xcss={paddingForProgressBar} />
				</>
			);
		}
		return (
			<>
				<CloseAndReturnButton onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep} />
				<StepProgressBar productSteps={productSteps} currentStepIndex={currentStepIndex} />
			</>
		);
	}, [productSteps, currentStepIndex, onCloseAndReturnToUploadStep]);

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.THIRD_PARTY_REVIEW_SCREEN}
			sourceType={SCREEN}
			attributes={{ thirdParty }}
		>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.third-party-review-screen.wrapper">
				{progressBarComponent}
				<ContentWrapper>{renderContent()}</ContentWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	width: '100%',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	margin: `${token('space.1000', '80px')} auto 0`,
	width: '80%',
	maxWidth: '712px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TileWrapper = styled.div({
	width: '100%',
	minWidth: '450px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	alignItems: 'end',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		maxWidth: '100%',
	},
});

const paddingForProgressBar = xcss({
	paddingTop: token('space.800', '64px'),
});
